import React, { useState } from 'react';
import { Toggle } from '../ui/Toggle';
import { Info } from 'lucide-react';
interface KYCFieldRowProps {
  label: string;
  isSelected: boolean;
  isMandatory: boolean;
  isOptional: boolean;
  onToggleSelect: (checked: boolean) => void;
  onToggleMandatory: (checked: boolean) => void;
  onToggleOptional: (checked: boolean) => void;
  fieldType?: 'text' | 'dropdown' | 'number' | 'email' | 'phone' | 'date' | 'file';
  regex?: string;
  explanation?: string;
  dropdownOptions?: string[];
}
export function KYCFieldRow({
  label,
  isSelected,
  isMandatory,
  isOptional,
  onToggleSelect,
  onToggleMandatory,
  onToggleOptional,
  fieldType = 'text',
  regex,
  explanation,
  dropdownOptions
}: KYCFieldRowProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const getDataTypeLabel = () => {
    switch (fieldType) {
      case 'text':
        return 'Text';
      case 'dropdown':
        return 'Dropdown / Select';
      case 'number':
        return 'Numeric';
      case 'date':
        return 'Date';
      case 'file':
        return 'File Upload';
      default:
        return 'Text';
    }
  };
  return <div className={`flex items-center justify-between py-4 px-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/30' : ''}`} data-id="element-400">
      <div className="flex items-center flex-1" data-id="element-401">
        <input type="checkbox" checked={isSelected} onChange={e => onToggleSelect(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer" data-id="element-402" />
        <span className={`ml-4 text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-403">
          {label}
        </span>

        {/* Info Icon with Tooltip */}
        <div className="relative ml-2" data-id="element-404">
          <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-gray-400 hover:text-blue-600 transition-colors" data-id="element-405">
            <Info className="h-4 w-4" data-id="element-406" />
          </button>

          {showTooltip && <div className="absolute left-0 top-6 z-50 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-xl" data-id="element-407">
              <div className="space-y-3" data-id="element-408">
                {/* Data Type */}
                <div data-id="element-409">
                  <span className="font-semibold text-blue-300" data-id="element-410">
                    Data Type:
                  </span>
                  <span className="ml-2" data-id="element-411">
                    {explanation || getDataTypeLabel()}
                  </span>
                </div>

                {/* Regex Pattern - only show if regex exists */}
                {regex && <div data-id="element-412">
                    <span className="font-semibold text-blue-300" data-id="element-413">Regex:</span>
                    <div className="mt-1 font-mono text-xs bg-gray-800 p-2 rounded break-all border border-gray-700" data-id="element-414">
                      {regex}
                    </div>
                  </div>}

                {/* Explanation - only show if regex exists */}
                {regex && explanation && <div data-id="element-415">
                    <span className="font-semibold text-blue-300" data-id="element-416">
                      Explanation:
                    </span>
                    <p className="mt-1 text-gray-300 leading-relaxed" data-id="element-417">
                      {explanation}
                    </p>
                  </div>}

                {/* Dropdown Options - only show if dropdown with options */}
                {fieldType === 'dropdown' && dropdownOptions && dropdownOptions.length > 0 && <div data-id="element-418">
                      <span className="font-semibold text-blue-300" data-id="element-419">
                        Options:
                      </span>
                      <div className="mt-1 pl-2 text-gray-300" data-id="element-420">
                        {dropdownOptions.map((opt, idx) => <div key={idx} data-id="element-421">• {opt}</div>)}
                      </div>
                    </div>}
              </div>
              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45" data-id="element-422"></div>
            </div>}
        </div>
      </div>

      <div className="flex items-center space-x-8" data-id="element-423">
        <div className="flex flex-col items-center w-24" data-id="element-424">
          <Toggle checked={isMandatory} onChange={onToggleMandatory} disabled={!isSelected} data-id="element-425" />
          <span className={`text-xs mt-1 ${isMandatory ? 'text-blue-600 font-medium' : 'text-gray-400'}`} data-id="element-426">
            Mandatory
          </span>
        </div>

        <div className="flex flex-col items-center w-24" data-id="element-427">
          <Toggle checked={isOptional} onChange={onToggleOptional} disabled={!isSelected} data-id="element-428" />
          <span className={`text-xs mt-1 ${isOptional ? 'text-blue-600 font-medium' : 'text-gray-400'}`} data-id="element-429">
            Optional
          </span>
        </div>
      </div>
    </div>;
}